import { EmojiEvents as Crown, Person as User, Edit as EditIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { AppearanceSettings } from '@/components/profile/AppearanceSettings';
import { DeleteAccountSection } from '@/components/profile/DeleteAccountSection';
import { PasswordSettings } from '@/components/profile/PasswordSettings';
import { SubscriptionInfo } from '@/components/profile/SubscriptionInfo';
import { useFirebase } from '@/firebase';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useSubscription } from '@/hooks/useSubscription';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

function ProfileContent() {
  const { t } = useTranslation();
  const { user: authUser, backend } = useFirebase();
  const { user: dbUser, loading } = useSubscription();
  const resolveResponsive = useResponsiveValue();

  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Sync displayName when user data resolves
  useEffect(() => {
    if (dbUser?.displayName) {
      setNewName(dbUser.displayName);
    } else if (authUser?.displayName) {
      setNewName(authUser.displayName);
    }
  }, [dbUser, authUser]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const handleSaveName = async () => {
    if (!newName.trim() || !backend) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await backend.billing.updateProfileDisplayName(newName.trim());
      setIsEditing(false);
      window.location.reload();
    } catch (err: unknown) {
      console.error('Failed to update display name:', err);
      setSaveError(t('profile.save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestEmailUpdate = async () => {
    if (!newEmail.trim() || !backend) return;
    setEmailSaving(true);
    setEmailError(null);
    setEmailSuccess(null);
    try {
      await backend.billing.requestContactEmailUpdate(newEmail.trim());
      setIsCodeSent(true);
      setEmailSuccess(t('profile.code_sent'));
    } catch (err: unknown) {
      console.error('Failed to request email update:', err);
      const errMsg = err instanceof Error ? err.message : '';
      setEmailError(errMsg || t('profile.email_request_error'));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleConfirmEmailUpdate = async () => {
    if (!verificationCode.trim() || !backend) return;
    setEmailSaving(true);
    setEmailError(null);
    setEmailSuccess(null);
    try {
      await backend.billing.confirmContactEmailUpdate(verificationCode.trim());
      setIsEditingEmail(false);
      setIsCodeSent(false);
      setVerificationCode('');
      window.location.reload();
    } catch (err: unknown) {
      console.error('Failed to confirm email update:', err);
      const errMsg = err instanceof Error ? err.message : '';
      setEmailError(errMsg || t('profile.email_confirm_error'));
    } finally {
      setEmailSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeName = dbUser?.displayName || authUser?.displayName || 'Anonymous User';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
            {t('profile.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('profile.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* User Info Card */}
          <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
            <Paper sx={{ p: 4, textAlign: 'center', height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <Avatar
                src={authUser?.photoURL ?? ''}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  fontSize: '3rem',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                {activeName ? getInitials(activeName) : <User sx={{ fontSize: 48 }} />}
              </Avatar>

              {isEditing ? (
                <Stack spacing={2} sx={{ mt: 2, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t('profile.enter_name')}
                    disabled={isSaving}
                    sx={{ width: '100%', maxWidth: 240 }}
                  />
                  {saveError && (
                    <Alert severity="error" sx={{ width: '100%', maxWidth: 240, py: 0 }}>
                      {saveError}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveName}
                      disabled={isSaving || !newName.trim()}
                      startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {t('common.save')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setIsEditing(false);
                        setNewName(activeName);
                        setSaveError(null);
                      }}
                      disabled={isSaving}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', mt: 2, mb: 1 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {activeName}
                  </Typography>
                  <IconButton id="edit-profile-name-btn" size="small" onClick={() => setIsEditing(true)}>
                    <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Stack>
              )}

              {isEditingEmail ? (
                <Stack spacing={2} sx={{ mt: 2, mb: 1, alignItems: 'center' }}>
                  {!isCodeSent ? (
                    <>
                      <TextField
                        size="small"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder={t('profile.enter_email')}
                        disabled={emailSaving}
                        sx={{ width: '100%', maxWidth: 240 }}
                      />
                      {emailError && (
                        <Alert severity="error" sx={{ width: '100%', maxWidth: 240, py: 0 }}>
                          {emailError}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleRequestEmailUpdate}
                          disabled={emailSaving || !newEmail.trim()}
                          startIcon={emailSaving ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {t('profile.send_code')}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsEditingEmail(false);
                            setNewEmail('');
                            setEmailError(null);
                            setEmailSuccess(null);
                          }}
                          disabled={emailSaving}
                        >
                          {t('common.cancel')}
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ maxWidth: 240 }} color="text.secondary">
                        {t('profile.enter_code_msg')} <strong>{newEmail}</strong>
                      </Typography>
                      <TextField
                        size="small"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder={t('profile.verification_code')}
                        disabled={emailSaving}
                        sx={{ width: '100%', maxWidth: 240 }}
                      />
                      {emailError && (
                        <Alert severity="error" sx={{ width: '100%', maxWidth: 240, py: 0 }}>
                          {emailError}
                        </Alert>
                      )}
                      {emailSuccess && (
                        <Alert severity="success" sx={{ width: '100%', maxWidth: 240, py: 0 }}>
                          {emailSuccess}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleConfirmEmailUpdate}
                          disabled={emailSaving || !verificationCode.trim()}
                          startIcon={emailSaving ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {t('common.confirm')}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsCodeSent(false);
                            setVerificationCode('');
                            setEmailError(null);
                            setEmailSuccess(null);
                          }}
                          disabled={emailSaving}
                        >
                          {t('common.back')}
                        </Button>
                      </Stack>
                    </>
                  )}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {dbUser?.contactEmail || authUser?.email}
                  </Typography>
                  <IconButton id="edit-profile-email-btn" size="small" onClick={() => {
                    setIsEditingEmail(true);
                    setNewEmail(dbUser?.contactEmail || authUser?.email || '');
                  }}>
                    <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Stack>
              )}

              <PasswordSettings user={authUser} />

              <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Crown sx={{ fontSize: 20, color: dbUser?.planId !== 'free' ? '#FFD700' : '#999' }} />
                  <Typography variant="body2" fontWeight={600}>
                    {dbUser?.planId
                      ? t(`pricing.plans.${dbUser.planId}.name`)
                      : dbUser?.planName}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Subscription Info Card */}
          <Grid size={resolveResponsive({ xs: 12, md: 8 })}>
            <SubscriptionInfo />
          </Grid>
        </Grid>
        <AppearanceSettings />
        <DeleteAccountSection />
      </Container>
    </Box>
  );
}

export default function ProfilePage() {
  return (
    <AuthGuard>
      <ProfileContent />
    </AuthGuard>
  );
}
